import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Volume2, VolumeX, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { logAdEvent, openAdDestination } from '../utils/adTracking';

// Video ads - randomly serve one
const videoAds = [
  {
    id: 1,
    name: 'Yellow Bird',
    url: 'https://customer-assets.emergentagent.com/job_2a4b6ebd-4542-4cf5-90b5-c4035b2eccae/artifacts/byrp6bjp_yellow%20bird%20video%201.mp4',
    accentColor: '#FCD34D'
  },
  {
    id: 2,
    name: 'Green Bird',
    url: 'https://customer-assets.emergentagent.com/job_2a4b6ebd-4542-4cf5-90b5-c4035b2eccae/artifacts/y44558ng_green%20bird%20video%201.mp4',
    accentColor: '#10B981'
  },
  {
    id: 3,
    name: 'Pink Bird',
    url: 'https://customer-assets.emergentagent.com/job_2a4b6ebd-4542-4cf5-90b5-c4035b2eccae/artifacts/9a91b3hv_pink%20bird%20video%201.mp4',
    accentColor: '#EC4899'
  },
  {
    id: 4,
    name: 'Blue Bird',
    url: 'https://customer-assets.emergentagent.com/job_2a4b6ebd-4542-4cf5-90b5-c4035b2eccae/artifacts/ta6jj8l1_blue%20bird%20video%201.mp4',
    accentColor: '#3B82F6'
  },
  {
    id: 5,
    name: 'Purple Bird',
    url: 'https://customer-assets.emergentagent.com/job_2a4b6ebd-4542-4cf5-90b5-c4035b2eccae/artifacts/u09g6xkk_purple%20bird%20video%201.mp4',
    accentColor: '#8B5CF6'
  },
  {
    id: 6,
    name: 'Brown Bird',
    url: 'https://customer-assets.emergentagent.com/job_2a4b6ebd-4542-4cf5-90b5-c4035b2eccae/artifacts/wn2ykdah_brown%20bird%20video%201.mp4',
    accentColor: '#A16207'
  }
];

const MockAdOverlay = ({ onClose, nickname }) => {
  const videoRef = useRef(null);
  const [countdown, setCountdown] = useState(5);
  const [currentAd, setCurrentAd] = useState(null);
  const [isMuted, setIsMuted] = useState(true);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);

  // Select random video on mount and log the impression (served to visitor)
  useEffect(() => {
    const randomIndex = Math.floor(Math.random() * videoAds.length);
    const ad = videoAds[randomIndex];
    setCurrentAd(ad);
    logAdEvent({ ad_id: ad.id, ad_name: ad.name, ad_type: 'video', placement: 'game-interstitial', kind: 'impression' });
  }, []);

  // Countdown timer
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Auto-play video when loaded
  useEffect(() => {
    if (videoRef.current && currentAd) {
      videoRef.current.play().catch(err => {
        console.log('Video autoplay failed:', err);
      });
    }
  }, [currentAd, isVideoLoaded]);

  const handleLearnMore = () => {
    if (currentAd) logAdEvent({ ad_id: currentAd.id, ad_name: currentAd.name, ad_type: 'video', placement: 'game-interstitial', kind: 'click' });
    // Open the advertiser post in a NEW TAB and dismiss the interstitial so the
    // player's game resumes right where they left off.
    openAdDestination();
    onClose();
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  if (!currentAd) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 backdrop-blur-md p-4"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ type: "spring", duration: 0.5 }}
          className="relative w-full max-w-lg"
        >
          {/* Main Card */}
          <div 
            className="relative overflow-hidden rounded-3xl shadow-2xl bg-gray-900"
            style={{
              boxShadow: `0 0 80px ${currentAd.accentColor}30, 0 25px 50px -12px rgba(0,0,0,0.5)`
            }}
          >
            {/* AD Label */}
            <div className="absolute top-4 left-4 z-20 px-3 py-1 bg-black/50 backdrop-blur-sm rounded-full text-white/70 text-xs font-semibold uppercase tracking-wider">
              Ad
            </div>

            {/* Close Button */}
            {countdown === 0 && (
              <motion.button
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                onClick={onClose}
                className="absolute top-4 right-4 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-black/50 hover:bg-black/70 transition-colors backdrop-blur-sm border border-white/20"
              >
                <X className="w-5 h-5 text-white" />
              </motion.button>
            )}

            {/* Countdown Badge */}
            {countdown > 0 && (
              <div className="absolute top-4 right-4 z-20 px-3 py-1 bg-black/50 backdrop-blur-sm rounded-full text-white text-sm font-semibold">
                Skip in {countdown}s
              </div>
            )}

            {/* Mute Button */}
            <button
              onClick={toggleMute}
              className="absolute bottom-32 right-4 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-black/50 hover:bg-black/70 transition-colors backdrop-blur-sm border border-white/20"
            >
              {isMuted ? (
                <VolumeX className="w-5 h-5 text-white" />
              ) : (
                <Volume2 className="w-5 h-5 text-white" />
              )}
            </button>

            {/* Video Container */}
            <div className="relative w-full aspect-[9/16] max-h-[60vh] bg-black">
              <video
                ref={videoRef}
                src={currentAd.url}
                className="w-full h-full object-contain"
                muted={isMuted}
                playsInline
                loop
                onLoadedData={() => setIsVideoLoaded(true)}
              />
              
              {/* Loading State */}
              {!isVideoLoaded && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent" />
                </div>
              )}
            </div>

            {/* Bottom Content */}
            <div className="relative z-10 p-6 bg-gradient-to-t from-gray-900 via-gray-900 to-transparent -mt-16 pt-20">
              {/* Message */}
              <div className="text-center mb-4">
                <h3 className="text-2xl sm:text-3xl font-heading font-bold text-white mb-1">
                  Reach local customers!
                </h3>
                <p className="text-white/70 text-lg">
                  Advertise in game.
                </p>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={handleLearnMore}
                  className="flex-1 bg-white hover:bg-white/90 text-gray-900 font-bold text-base px-6 py-5 rounded-xl shadow-lg"
                >
                  Learn More
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                
                {countdown === 0 && (
                  <Button
                    variant="outline"
                    onClick={onClose}
                    className="flex-1 border-white/30 text-white hover:bg-white/10 font-semibold text-base px-6 py-5 rounded-xl"
                  >
                    Continue Playing
                  </Button>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default MockAdOverlay;
